import axios from 'axios';
import { getBaseUrl } from './utils/config';
import { getStoredAuthToken } from './utils/secureStorage';
import { applyApiCryptoInterceptors } from './utils/apiCrypto';

const baseURL = getBaseUrl();

// Axios instance
const apiClient = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
    'x-session-header': 'customer-9319737c11894da8a94643d11ec56e7d'
  },
});

// Attach auth token (if any) to every request
apiClient.interceptors.request.use((config) => {
  const token = getStoredAuthToken();
  if (token) {
    // eslint-disable-next-line no-param-reassign
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Default Content-Type is application/json — that breaks FormData (no multipart boundary).
  // Axios treats Content-Type `false` as "do not set" so the browser sets multipart correctly.
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    if (typeof config.headers?.set === 'function') {
      config.headers.set('Content-Type', false);
    } else {
      // eslint-disable-next-line no-param-reassign
      config.headers['Content-Type'] = false;
    }
  }
  return config;
});

applyApiCryptoInterceptors(apiClient);

// ===== General helper wrappers =====

// Cities & Categories
export const getCities = (page = 0, pageSize = 100) =>
  apiClient.get('/customer/cities', { params: { Page: page, PageSize: pageSize } });

export const getAppCategories = (cityId, page = 0, pageSize = 100) =>
  apiClient.get('/GetSettled/AppCategories', { params: { CityId: cityId, Page: page, PageSize: pageSize } });

// Auth
export const sendEmailOtp = (email) => apiClient.post('/customer/Authentication/SendOtp', { email });
export const sendSmsOtp = (phoneNumber) => apiClient.post('/customer/Authentication/SendSmsOtp', { phoneNumber });
// Signup OTP flow (new API contract)
export const sendSignupOtps = (email, phoneNumber) =>
  apiClient.post('/customer/Authentication/signup/send-otps', { email, phoneNumber });
export const sendSignupEmailOtp = (email) =>
  apiClient.post('/customer/Authentication/signup/send-email-otp', { email });
export const sendPhoneOtp = (email, phoneNumber) =>
  apiClient.post('/customer/Authentication/send-phone-otp', { email, phoneNumber });
export const verifyEmail = (email, otp) =>
  apiClient.post('/customer/Authentication/signup/verify-email', { email, otp, emailOtp: otp });
export const verifyPhone = (email, phoneNumber, otp) =>
  apiClient.post('/customer/Authentication/signup/verify-phone', { email, phoneNumber, otp });
export const signup = (payload) => apiClient.post('/customer/Authentication/signup', payload);
export const loginApi = (email, password) => apiClient.post('/customer/Authentication/Login', { email, password });
/** POST /api/Authentication/ForgetPassword — sends OTP to email */
export const forgotPassword = (email) => apiClient.post('/Authentication/ForgetPassword', { email });

/** POST /api/customer/Authentication/reset-password — complete reset with OTP */
export const resetPasswordWithOtp = (payload) =>
  apiClient.post('/customer/Authentication/reset-password', payload);

// Contacts
export const fetchContacts = () => apiClient.get('/Contacts');
export const createContact = (payload) => apiClient.post('/Contacts', payload);
export const updateContact = (id, payload) => apiClient.put(`/Contacts/${id}`, payload);
export const deleteContact = (id) => apiClient.delete(`/Contacts/${id}`);

// Booking Requests
export const fetchBookingRequests = () => apiClient.get('/BookingRequests');
export const updateBookingRequestStatus = (id, status) =>
  apiClient.put(`/BookingRequests/${id}`, { status });

// Legal Documents
export const getLatestPrivacy = () => apiClient.get('/Privacy/customer/latest');
export const getLatestTerms = () => apiClient.get('/Terms/customer/latest');

// Slots, Templates, etc., can be added here similarly.

export default apiClient;
